import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Link } from 'react-router-dom';
import ButtonElement from '@/components/elements/Button/Button';

/**
 * A compact floating card (not a full-width bar). "Manage Preferences" expands the same
 * accurate "what we store" breakdown the old banner had — this site has no per-category
 * toggles (only accept/decline), so it stays an explanation, not a preference form that
 * would imply controls we don't actually have.
 */
const CookieConsentBanner: React.FC = () => {
  const { showBanner, consentToCookies, denyCookies } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          role="region"
          aria-label="Cookie preferences"
          className="fixed inset-x-4 bottom-4 z-50 sm:inset-x-auto sm:left-6 sm:bottom-6 sm:w-[420px]"
        >
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              {/* Icon badge */}
              <div className="relative flex-shrink-0">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-custom-light">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                      d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2Z"
                      fill="#C17A3D"
                      stroke="#8B4513"
                      strokeWidth="0.5"
                    />
                    <path d="M18 6C18 6 17.5 5.5 17 6C16.5 6.5 16.5 7 17 7.5C17.5 8 18 8 18.5 7.5C19 7 19 6.5 18 6Z" fill="#8B4513" />
                    <circle cx="7" cy="8" r="1.2" fill="#654321" />
                    <circle cx="16" cy="9" r="1.2" fill="#654321" />
                    <circle cx="9" cy="15" r="1.2" fill="#654321" />
                    <circle cx="15" cy="16" r="1.2" fill="#654321" />
                    <circle cx="12" cy="6" r="1.2" fill="#654321" />
                    <circle cx="6" cy="12" r="1.2" fill="#654321" />
                    <circle cx="18" cy="13" r="1.2" fill="#654321" />
                    <circle cx="11" cy="18" r="1.2" fill="#654321" />
                  </svg>
                </div>
                <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-custom-secolor ring-2 ring-white">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
              </div>

              {/* Heading + description */}
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-custom-darkblue">Cookie Preferences</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                  We don&apos;t use tracking or advertising cookies — only what&apos;s needed to remember
                  optional preferences, like the product category you last viewed. Learn more in our{' '}
                  <Link to="/cookie-policy" className="font-semibold text-custom-secolor hover:underline">
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* "Manage Preferences" expands this — the real, complete list of what's stored. */}
            <AnimatePresence initial={false}>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                    <h4 className="mb-1 text-sm font-semibold text-custom-secolor">What we store on your device:</h4>
                    <ul className="space-y-1 text-xs text-gray-600">
                      <li>
                        • <strong>Always:</strong> your choice on this card, so we don&apos;t ask again on every visit.
                      </li>
                      <li>
                        • <strong>Only if you accept:</strong> the product category you last viewed.
                      </li>
                      <li>
                        • <strong>Until you close the tab:</strong> the filters you pick while browsing products and
                        projects.
                      </li>
                    </ul>
                    <p className="mt-2 text-xs text-gray-600">
                      We use no analytics or advertising services. The map (provided by Google) only loads if you
                      choose to show it.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setShowDetails((value) => !value)}
                aria-expanded={showDetails}
                className="text-sm font-semibold text-gray-500 hover:text-custom-secolor"
              >
                {showDetails ? 'Hide Preferences' : 'Manage Preferences'}
              </button>

              <div className="flex flex-shrink-0 items-center gap-4">
                <button
                  type="button"
                  onClick={denyCookies}
                  className="text-sm font-semibold text-custom-darkblue hover:text-custom-secolor"
                >
                  Decline
                </button>
                <ButtonElement
                  onClick={consentToCookies}
                  size="small"
                  btnText={
                    <span className="inline-flex items-center gap-1.5 text-sm">
                      Accept All
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                  }
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
